import React, { useState, useEffect, useCallback } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { StatusActionButton, StatsGrid, StatItem, HorizontalCategoryTabs } from '../../../shared/ui';
import { questionsV2Api, QuestionV2, QuestionStatistics } from '../../../api/questionsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useDashboardCounters } from '../../../shared/contexts/DashboardCountersContext';
import CreateQuestionV2Modal from './CreateQuestionV2Modal';
import QuestionV2MessengerModal from './QuestionV2MessengerModal';
import { useOptimizedSignalR, QuestionStatusChangedEvent, QuestionMemberEvent } from '../../../hooks/useOptimizedSignalR';
import './QuestionsV2Card.css';

interface QuestionsV2CardProps {
  siteId: number;
}

const QuestionsV2Card: React.FC<QuestionsV2CardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true); // Ленивая загрузка: по умолчанию свернута
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false); // Отслеживаем открытие fullscreen
  const [questions, setQuestions] = useState<QuestionV2[]>([]);
  const [statistics, setStatistics] = useState<QuestionStatistics | null>(null);
  const [loading, setLoading] = useState(false); // Начинаем с false, т.к. не загружаем сразу
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMessengerModal, setShowMessengerModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<QuestionV2 | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'closed' | 'my'>('all');
  const { user } = useAuth();
  const countersContext = useDashboardCounters();
  
  // Получаем счетчик из Context (если доступен) или null
  const counter = countersContext?.counters?.openQuestions.hasAccess 
    ? countersContext.counters.openQuestions.count 
    : null;
  const countersLoading = countersContext?.loading ?? false;

  // Загрузка данных
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Загружаем только вопросы (typeId = 1)
      const questionsData = await questionsV2Api.getSiteContentByTypeV2(siteId, 1);
      
      setQuestions(questionsData);
      
      // Вычисляем статистику из загруженных данных
      const stats: QuestionStatistics = {
        total: questionsData.length,
        open: questionsData.filter((q: QuestionV2) => !q.closed).length,
        closed: questionsData.filter((q: QuestionV2) => q.closed).length,
        my: questionsData.filter((q: QuestionV2) => q.userId === user?.id).length
      };
      setStatistics(stats);
    } catch (err) {
      setError('Ошибка загрузки дискуссий');
      console.error('Error loading questions:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId, user?.id]);

  // Счетчики загружаются через Context в ObjectPage

  // Ленивая загрузка: загружаем данные когда карточка раскрыта (inline) или fullscreen модальное окно открыто
  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      loadData();
    }
  }, [isCollapsed, isFullscreenOpen, loadData]);

  // Обработчик SignalR событий для обновления статуса вопросов
  const handleQuestionStatusChanged = useCallback((questionEvent: QuestionStatusChangedEvent) => {
    console.log('🔔 QuestionsV2Card: получено SignalR событие', questionEvent);
    
    // Извлекаем siteId из объекта Site (сервер отправляет QuestionModelBl, а не QuestionStatusChangedEvent)
    const eventSiteId = questionEvent.site?.id || questionEvent.siteId;
    
    // Обновляем только вопросы для текущего объекта
    if (eventSiteId === siteId) {
      console.log('✅ QuestionsV2Card: обновляем вопрос', questionEvent.id, 'статус:', questionEvent.closed);
      
      setQuestions(prevQuestions => 
        prevQuestions.map(question => 
          question.id === questionEvent.id 
            ? { ...question, closed: questionEvent.closed }
            : question
        )
      );
      
      // Обновляем статистику асинхронно
      setTimeout(() => loadData(), 0);
    } else {
      console.log('❌ QuestionsV2Card: событие не для текущего объекта', eventSiteId, 'текущий:', siteId);
    }
  }, [siteId]); // Убираем loadData из зависимостей

  // Обработчик SignalR событий для создания нового вопроса
  const handleQuestionCreatedSignalR = useCallback((question: QuestionStatusChangedEvent) => {
    console.log('🔔 QuestionsV2Card: получено событие создания вопроса', question);
    
    // Проверяем, что вопрос относится к текущему объекту
    const eventSiteId = question.site?.id;
    if (eventSiteId === siteId) {
      console.log('✅ QuestionsV2Card: обновляем список вопросов после создания');
      // Перезагружаем данные для обновления списка вопросов асинхронно
      setTimeout(() => loadData(), 0);
    } else {
      console.log('❌ QuestionsV2Card: событие не для текущего объекта', eventSiteId, 'текущий:', siteId);
    }
  }, [siteId]); // Убираем loadData из зависимостей

  // Обработчик SignalR событий для добавления участника к вопросу
  const handleQuestionMemberAddedSignalR = useCallback((data: QuestionMemberEvent) => {
    console.log('🔔 QuestionsV2Card: получено событие добавления участника', data);
    
    // Проверяем, что вопрос относится к текущему объекту
    const eventSiteId = data.question?.site?.id;
    if (eventSiteId === siteId) {
      console.log('✅ QuestionsV2Card: обновляем список вопросов после добавления участника');
      // Перезагружаем данные для обновления списка вопросов асинхронно
      setTimeout(() => loadData(), 0);
    } else {
      console.log('❌ QuestionsV2Card: событие не для текущего объекта', eventSiteId, 'текущий:', siteId);
    }
  }, [siteId]); // Убираем loadData из зависимостей

  // Обработчик SignalR событий для удаления участника из вопроса
  const handleQuestionMemberRemovedSignalR = useCallback((data: QuestionMemberEvent) => {
    console.log('🔔 QuestionsV2Card: получено событие удаления участника', data);
    
    // Проверяем, что вопрос относится к текущему объекту
    const eventSiteId = data.question?.site?.id;
    if (eventSiteId === siteId) {
      console.log('✅ QuestionsV2Card: обновляем список вопросов после удаления участника');
      // Перезагружаем данные для обновления списка вопросов асинхронно
      setTimeout(() => loadData(), 0);
    } else {
      console.log('❌ QuestionsV2Card: событие не для текущего объекта', eventSiteId, 'текущий:', siteId);
    }
  }, [siteId]); // Убираем loadData из зависимостей

  // Используем оптимизированный SignalR хук для подписки на события сайта
  // Используем уникальный subscriptionId для этого компонента
  useOptimizedSignalR({
    onQuestionStatusChanged: handleQuestionStatusChanged,
    onQuestionMemberAdded: handleQuestionMemberAddedSignalR,
    onQuestionMemberRemoved: handleQuestionMemberRemovedSignalR,
    onQuestionCreated: handleQuestionCreatedSignalR,
  }, {
    siteId,
    subscriptionId: `questions_site_${siteId}`
  });

  // Фильтрация вопросов
  const filteredQuestions = questions.filter(question => {
    switch (filter) {
      case 'open':
        return !question.closed;
      case 'closed':
        return question.closed;
      case 'my':
        return user && question.userId === user.id;
      default:
        return true;
    }
  });

  // Создание элементов списка
  const createQuestionItems = (): QuestionV2[] => {
    return filteredQuestions;
  };

  // Обработчики событий
  const handleCreateQuestion = () => {
    setShowCreateModal(true);
  };

  const handleOpenMessenger = (question: QuestionV2) => {
    setSelectedQuestion(question);
    setShowMessengerModal(true);
  };

  const handleQuestionCreated = () => {
    setShowCreateModal(false);
    loadData();
  };


  const handleQuestionUpdated = () => {
    loadData();
  };

  // Подготовка данных для статистики
  const statsData: StatItem[] = [
    {
      id: 'total',
      value: statistics?.total || 0,
      label: 'Всего',
      icon: 'ri-question-line',
      color: 'muted'
    },
    {
      id: 'open',
      value: statistics?.open || 0,
      label: 'Открытых',
      icon: 'ri-lock-unlock-line',
      color: 'success'
    },
    {
      id: 'closed',
      value: statistics?.closed || 0,
      label: 'Закрытых',
      icon: 'ri-lock-line',
      color: 'muted'
    },
    {
      id: 'my',
      value: statistics?.my || 0,
      label: 'Мои',
      icon: 'ri-user-line',
      color: 'primary'
    }
  ];

  return (
    <DashboardCard
      id="questions-v2-card"
      title="Дискуссии"
      icon="ri-discuss-line"
      isCollapsible={true}
      expandMode="fullscreen"
      compactTileVariant="discussions"
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      onFullscreenOpenChange={setIsFullscreenOpen}
      counters={counter !== null ? [counter] : undefined}
      countersLoading={countersLoading}
    >
      {/* Кнопка создания вопроса */}
      <div style={{ padding: '0 0 20px 0' }}>
        <StatusActionButton
          variant="primary"
          fullWidth
          onClick={handleCreateQuestion}
        >
          Создать дискуссию
        </StatusActionButton>
      </div>

      {/* Статистика */}
      <StatsGrid items={statsData} />

      {/* Фильтры */}
      <HorizontalCategoryTabs
        categories={[
          `Открытые (${statistics?.open || 0})`,
          `Закрытые (${statistics?.closed || 0})`,
          `Мои (${statistics?.my || 0})`
        ]}
        value={
          filter === 'all' 
            ? null 
            : filter === 'open' 
              ? `Открытые (${statistics?.open || 0})`
              : filter === 'closed'
                ? `Закрытые (${statistics?.closed || 0})`
                : `Мои (${statistics?.my || 0})`
        }
        onChange={(category) => {
          if (category === null) {
            setFilter('all');
          } else if (category.startsWith('Открытые')) {
            setFilter('open');
          } else if (category.startsWith('Закрытые')) {
            setFilter('closed');
          } else if (category.startsWith('Мои')) {
            setFilter('my');
          }
        }}
        showAllOption={true}
        allOptionLabel={`Все (${statistics?.total || 0})`}
        size="medium"
        className="questions-filters"
        style={{ marginBottom: '20px' }}
      />

      {/* Список вопросов */}
      <div className="questions-list">
        {loading ? (
          <div className="questions-loading">
            <i className="ri-loader-4-line ri-spin"></i>
            <span>Загрузка дискуссий...</span>
          </div>
        ) : createQuestionItems().length === 0 ? (
          <div className="questions-empty">
            <i className="ri-question-line"></i>
            <span>Дискуссий пока нет</span>
            <small>Создайте первую дискуссию, чтобы начать работу</small>
          </div>
        ) : (
          createQuestionItems().map((question) => (
            <div
              key={question.id}
              className={`question-item ${question.closed ? 'closed' : ''}`}
              onClick={() => handleOpenMessenger(question)}
            >
              <div className="question-avatar">
                <i className={question.closed ? 'ri-lock-line' : 'ri-question-line'}></i>
              </div>
              
              <div className="question-content">
                <h3 className="question-title">{question.text}</h3>
                <div className="question-subtitle">
                  {question.user?.name || 'Неизвестный'} • {question.typeOfWorks?.name || 'Без типа работы'}
                </div>
                <div className="question-details">
                  <span className="question-detail">
                    <i className="ri-time-line"></i>
                    {question.createdAt ? new Date(question.createdAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Дата неизвестна'}
                  </span>
                </div>
              </div>
              
              <div className="question-arrow">
                <i className="ri-arrow-right-s-line"></i>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Модальное окно создания вопроса */}
      <CreateQuestionV2Modal
        isOpen={showCreateModal}
        siteId={siteId}
        typeId={1} // 1 = Question
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleQuestionCreated}
      />

      {/* Модальное окно с мессенджером */}
      <QuestionV2MessengerModal
        isOpen={showMessengerModal}
        question={selectedQuestion}
        siteId={siteId}
        onClose={() => {
          setShowMessengerModal(false);
          setSelectedQuestion(null);
        }}
        onQuestionUpdated={handleQuestionUpdated}
      />
    </DashboardCard>
  );
};

export default QuestionsV2Card;
